/**
 * HookUpPanel — F.5 Hook-Up Generator template viewer per
 * `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.1 / §6.2.
 *
 * Left column: list of 20+ hook-up templates (from /hook-ups/templates).
 * Right column: SVG preview for the selected template + instrument tag +
 * line. Below: an editable table (the "index") of every instrument that
 * needs a hook-up, persisted via /hookups-index.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Download, Plus, Trash2, Save, FileSignature, Wand2 } from "lucide-react";
import api, { API_BASE, formatErr } from "../lib/api";

export default function HookUpPanel({ projectId }) {
  const [templates, setTemplates] = useState([]);
  const [sel, setSel] = useState(null);
  const [tag, setTag] = useState("");
  const [line, setLine] = useState("");
  const [svg, setSvg] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const show = (t, kind = "ok") => {
    setToast({ t, kind });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      try {
        const [tpl, idx] = await Promise.all([
          api.get(`/projects/${projectId}/hook-ups/templates`),
          api.get(`/projects/${projectId}/hookups-index`),
        ]);
        setTemplates(tpl.data || []);
        setRows(idx.data?.items || []);
        if ((tpl.data || []).length) {
          setSel(tpl.data[0].id);
          setTag("FT-1001");
          setLine("10\"-PR-1001-A1A");
        }
      } catch (e) {
        show(formatErr(e?.response?.data?.detail) || e.message, "err");
      }
    })();
  }, [projectId]);

  const preview = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      const resp = await api.post(`/projects/${projectId}/hook-ups.svg`,
                                    { template: sel, tag, line },
                                    { responseType: "blob" });
      const text = await resp.data.text();
      setSvg(text);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (sel && tag) preview();
    // eslint-disable-next-line
  }, [sel]);

  const download = () => {
    if (!svg) return;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `HU-${tag}-${sel}.svg`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const addRow = () => setRows([...rows, { tag: "", template: sel || "LT-DP",
                                              service: "", line: "" }]);
  const upd = (i, p) => setRows(rows.map((r, j) => i === j ? { ...r, ...p } : r));
  const del = (i) => setRows(rows.filter((_, j) => j !== i));

  const saveIndex = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/hookups-index`, { items: rows });
      show(`Saved ${rows.length} hook-up rows`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  const autofill = async () => {
    // Seed the index from PDO instruments (uses /loops/generate response)
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/loops/generate`);
      const loops = r.data?.loops || [];
      const next = loops.map((l) => ({
        tag: l.tag, template: _guessTemplate(l.tag), service: l.process_fn || "",
        line: "",
      }));
      setRows(next);
      show(`Generated ${next.length} hook-up rows from PDO instruments`);
    } catch (e) {
      show(formatErr(e?.response?.data?.detail) || e.message, "err");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="hookup-panel"
         style={{ display: "flex", flexDirection: "column", gap: 8,
                   height: "calc(100vh - 260px)", minHeight: 600 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8,
                     padding: "6px 10px", borderBottom: "1px solid var(--pf-border)",
                     background: "var(--pf-surface-2)" }}>
        <FileSignature size={14} color="var(--pf-accent)" />
        <span className="pf-overline" style={{ margin: 0 }}>
          HOOK-UP GENERATOR (F.5) — {templates.length} templates · {rows.length} rows
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost" data-testid="hookup-autofill-btn"
                 onClick={autofill} disabled={busy}
                 style={btn}><Wand2 size={12}/> Auto-fill from PDO</button>
        <button className="btn btn-ghost" data-testid="hookup-download-btn"
                 onClick={download} disabled={!svg}
                 style={btn}><Download size={12}/> Download SVG</button>
        <button className="btn btn-primary" data-testid="hookup-save-btn"
                 onClick={saveIndex} disabled={busy}
                 style={btn}>
          {busy ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save Index
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flex: 1, minHeight: 0 }}>
        {/* Left: template list */}
        <div data-testid="hookup-template-list" style={{
          width: 200, overflow: "auto",
          background: "var(--pf-surface)", border: "1px solid var(--pf-border)",
          borderRadius: 4, padding: 6,
        }}>
          <div className="pf-overline" style={{ marginBottom: 6 }}>TEMPLATES</div>
          {templates.map((t) => (
            <button
              key={t.id}
              data-testid={`hookup-template-${t.id}`}
              onClick={() => setSel(t.id)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "6px 8px", marginBottom: 2,
                background: sel === t.id ? "var(--pf-accent)" : "transparent",
                color: sel === t.id ? "#0E1218" : "var(--pf-fg)",
                border: `1px solid ${sel === t.id ? "var(--pf-accent)" : "transparent"}`,
                borderRadius: 3,
                fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
                cursor: "pointer",
              }}
            >
              <div style={{ fontWeight: 700 }}>{t.id}</div>
              <div style={{ fontSize: 10, opacity: 0.8,
                              color: sel === t.id ? "#0E1218" : "var(--pf-text-dim)" }}>
                {t.label}
              </div>
            </button>
          ))}
        </div>

        {/* Middle: preview */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column",
                       background: "var(--pf-surface)",
                       border: "1px solid var(--pf-border)", borderRadius: 4,
                       minWidth: 0 }}>
          <div style={{ display: "flex", gap: 6, padding: "6px 10px",
                          borderBottom: "1px solid var(--pf-border)",
                          background: "var(--pf-surface-2)" }}>
            <input placeholder="Instrument tag (e.g. FT-1001)"
                    data-testid="hookup-tag" value={tag}
                    onChange={(e) => setTag(e.target.value)} style={inp} />
            <input placeholder='Process line (e.g. 10"-PR-1001-A1A)'
                    data-testid="hookup-line" value={line}
                    onChange={(e) => setLine(e.target.value)}
                    style={{ ...inp, flex: 1 }} />
            <button className="btn btn-ghost" data-testid="hookup-preview-btn"
                     onClick={preview} disabled={busy || !sel}
                     style={btn}>
              {busy ? <Loader2 className="pf-spin" size={12}/> : null} Preview
            </button>
          </div>
          <div style={{ flex: 1, overflow: "auto", background: "#0d0f12",
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
            {svg ? (
              <div style={{ width: "100%", height: "100%",
                              display: "flex", justifyContent: "center", padding: 20 }}
                    dangerouslySetInnerHTML={{ __html: svg }} />
            ) : (
              <div style={{ color: "var(--pf-text-dim)", fontSize: 12,
                             fontFamily: "'JetBrains Mono', monospace" }}>
                Pick a template and click Preview to render the hook-up SVG.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom: editable index */}
      <div style={{ overflow: "auto", border: "1px solid var(--pf-border)",
                     borderRadius: 4, maxHeight: 260 }}>
        <table style={{ width: "100%", borderCollapse: "collapse",
                          fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
          <thead style={{ background: "var(--pf-surface-2)",
                           position: "sticky", top: 0 }}>
            <tr>
              {["Tag", "Template", "Service", "Line", "Sheet", ""].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`hookup-row-${i}`}>
                <td style={td}><input style={cellInp}
                         data-testid={`hookup-${i}-tag`}
                         value={r.tag}
                         onChange={(e) => upd(i, { tag: e.target.value })}/></td>
                <td style={td}>
                  <select style={cellInp} value={r.template || ""}
                           data-testid={`hookup-${i}-template`}
                           onChange={(e) => upd(i, { template: e.target.value })}>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.id}</option>)}
                  </select>
                </td>
                <td style={td}><input style={cellInp}
                         value={r.service || ""}
                         onChange={(e) => upd(i, { service: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         value={r.line || ""}
                         onChange={(e) => upd(i, { line: e.target.value })}/></td>
                <td style={td}><input style={cellInp}
                         value={r.sheet || ""}
                         onChange={(e) => upd(i, { sheet: e.target.value })}/></td>
                <td style={td}>
                  <button className="btn btn-ghost"
                           data-testid={`hookup-${i}-del`}
                           onClick={() => del(i)}
                           style={{ ...btn, color: "#ef4444", padding: "2px 4px" }}>
                    <Trash2 size={10}/>
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} style={{ ...td, textAlign: "center",
                                               color: "var(--pf-text-dim)" }}>
                No hook-ups indexed. Click "Auto-fill from PDO" or "+".
              </td></tr>
            )}
          </tbody>
        </table>
        <div style={{ padding: 6 }}>
          <button className="btn btn-ghost" onClick={addRow}
                   data-testid="hookup-add-row"
                   style={{ ...btn, borderStyle: "dashed" }}>
            <Plus size={11}/> Add row
          </button>
        </div>
      </div>

      {toast && (
        <div data-testid={`hookup-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 28, right: 28,
          background: toast.kind === "ok" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)",
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          padding: "8px 14px", fontSize: 12, borderRadius: 3, zIndex: 10001,
          fontFamily: "'JetBrains Mono', monospace",
        }}>{toast.t}</div>
      )}
    </div>
  );
}

function _guessTemplate(tag) {
  const t = (tag || "").toUpperCase();
  if (t.startsWith("LT") || t.startsWith("LIT")) return "LT-DP";
  if (t.startsWith("PT") || t.startsWith("PIT")) return "PT-LIQ";
  if (t.startsWith("TT") || t.startsWith("TIT")) return "TT-RTD";
  if (t.startsWith("FT") || t.startsWith("FIT")) return "FT-DP";
  if (t.startsWith("AT") || t.startsWith("AIT")) return "AI-pH";
  if (t.startsWith("PSV")) return "PSV-LIQ";
  if (t.startsWith("CV")) return "CV-GLOBE";
  if (t.startsWith("BV") || t.startsWith("XV") || t.startsWith("SDV")) return "BV-ON-OFF";
  return "LT-DP";
}

const btn = { fontSize: 11, padding: "4px 10px", borderRadius: 3 };
const inp = { background: "var(--pf-surface-2)", border: "1px solid var(--pf-border)",
               color: "var(--pf-fg)", padding: "4px 8px", fontSize: 11,
               borderRadius: 3, fontFamily: "'JetBrains Mono', monospace" };
const th = { padding: "6px 8px", textAlign: "left", color: "var(--pf-text-dim)",
              borderBottom: "1px solid var(--pf-border)", fontSize: 10,
              whiteSpace: "nowrap", textTransform: "uppercase" };
const td = { padding: 4, borderBottom: "1px solid var(--pf-border-dim, #222)",
              verticalAlign: "top" };
const cellInp = { ...inp, width: "100%", minWidth: 80, fontSize: 10, padding: "2px 4px" };
